const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID || '';
const ANALYTICS_ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';

const GA_ID_PATTERN = /^G-[A-Z0-9]+$/i;
const ADS_ID_PATTERN = /^AW-\d+$/i;
let initialized = false;
let lastPageView = '';
let removeClickTracking = null;

export function isValidGaMeasurementId(value) {
  return GA_ID_PATTERN.test(String(value || '').trim());
}

export function isValidGoogleAdsId(value) {
  return ADS_ID_PATTERN.test(String(value || '').trim());
}

function isTrackingAllowed() {
  if (!ANALYTICS_ENABLED || typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  return window.navigator?.doNotTrack !== '1';
}

function googleTag(...args) {
  if (!isTrackingAllowed()) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function adsDestination(label) {
  const normalizedLabel = String(label || '').trim();
  if (!isValidGoogleAdsId(GOOGLE_ADS_ID) || !normalizedLabel) return '';
  if (normalizedLabel.startsWith('AW-')) return normalizedLabel;
  return `${GOOGLE_ADS_ID}/${normalizedLabel}`;
}

function sendEvent(name, parameters = {}, adsLabel = '') {
  if (!initialized || !name) return;

  const destination = adsDestination(adsLabel);
  const sendTo = destination && isValidGaMeasurementId(GA_MEASUREMENT_ID)
    ? [GA_MEASUREMENT_ID, destination]
    : destination;
  const payload = sendTo ? { ...parameters, send_to: sendTo } : parameters;
  googleTag('event', name, payload);
}

function trackLead({ leadType, method, value, adsLabel }) {
  const numericValue = Number(value);
  sendEvent('generate_lead', {
    currency: 'USD',
    lead_type: leadType,
    method,
    ...(Number.isFinite(numericValue) && numericValue > 0 ? { value: numericValue } : {}),
  }, adsLabel);
}

function installLinkTracking() {
  if (removeClickTracking || typeof document === 'undefined') return;

  const handleClick = (event) => {
    const element = event.target instanceof Element ? event.target : event.target?.parentElement;
    const anchor = element?.closest?.('a[href]');
    const href = anchor?.getAttribute('href') || '';
    if (!href) return;

    if (/^https?:\/\/(?:api\.)?wa\.me\//i.test(href)) {
      trackLead({
        leadType: 'whatsapp_click',
        method: 'WhatsApp',
        adsLabel: process.env.REACT_APP_GOOGLE_ADS_WHATSAPP_LABEL,
      });
      return;
    }

    if (/^tel:/i.test(href)) {
      trackLead({
        leadType: 'phone_click',
        method: 'Phone',
        adsLabel: process.env.REACT_APP_GOOGLE_ADS_PHONE_LABEL,
      });
      return;
    }

    if (/^mailto:/i.test(href)) {
      trackLead({
        leadType: 'email_click',
        method: 'Email',
        adsLabel: process.env.REACT_APP_GOOGLE_ADS_EMAIL_LABEL,
      });
    }
  };

  document.addEventListener('click', handleClick, true);
  removeClickTracking = () => {
    document.removeEventListener('click', handleClick, true);
    removeClickTracking = null;
  };
}

export function initializeAnalytics() {
  if (initialized || !isTrackingAllowed()) return initialized;

  const validGaId = isValidGaMeasurementId(GA_MEASUREMENT_ID);
  const validAdsId = isValidGoogleAdsId(GOOGLE_ADS_ID);
  if (!validGaId && !validAdsId) return false;

  const primaryId = validGaId ? GA_MEASUREMENT_ID : GOOGLE_ADS_ID;
  const script = document.createElement('script');
  script.id = 'deroyalty-google-tag';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(primaryId)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  googleTag('js', new Date());

  if (validGaId) {
    googleTag('config', GA_MEASUREMENT_ID, {
      send_page_view: false,
      anonymize_ip: true,
    });
  }

  if (validAdsId) {
    googleTag('config', GOOGLE_ADS_ID);
  }

  initialized = true;
  installLinkTracking();
  return true;
}

export function trackPageView({ location, title }) {
  if (!initialized || !location || location === lastPageView) return;
  lastPageView = location;
  sendEvent('page_view', {
    page_location: location,
    page_title: title || document.title,
  });
}

export function toAnalyticsItem(product, quantity = 1) {
  const price = Number(product?.price);
  return {
    item_id: String(product?.id || product?.productId || 'vehicle'),
    item_name: product?.name || 'Rental vehicle',
    item_brand: 'DeRoyalty Car Rental',
    item_category: product?.categoryName || product?.category?.categoryName || 'Vehicle rental',
    price: Number.isFinite(price) ? price : 0,
    quantity: Math.max(1, Number(quantity) || 1),
  };
}

export function trackVehicleView(product) {
  const value = Number(product?.price);
  sendEvent('view_item', {
    currency: 'USD',
    value: Number.isFinite(value) ? value : 0,
    items: [toAnalyticsItem(product)],
  });
}

export function trackAddToCart(product, rentalDays = 1) {
  const days = Math.max(1, Number(rentalDays) || 1);
  const price = Number(product?.price);
  sendEvent('add_to_cart', {
    currency: 'USD',
    value: Number.isFinite(price) ? price * days : 0,
    items: [toAnalyticsItem(product, days)],
  });
}

export function trackBeginCheckout(items = [], value = 0) {
  sendEvent('begin_checkout', {
    currency: 'USD',
    value: Number(value) || 0,
    items: items.map((item) => toAnalyticsItem(item.product || item, item.quantity || item.booked_days)),
  });
}

export function trackBookingConfirmation(items = [], value = 0) {
  const numericValue = Number(value) || 0;
  const analyticsItems = items.map((item) => toAnalyticsItem(item.product || item, item.quantity || item.booked_days));

  sendEvent('booking_confirmation_reached', {
    currency: 'USD',
    value: numericValue,
    items: analyticsItems,
  });

  trackLead({
    leadType: 'booking_confirmation',
    method: 'Website checkout',
    value: numericValue,
    adsLabel: process.env.REACT_APP_GOOGLE_ADS_BOOKING_LABEL,
  });
}

export function trackContactSubmission(requestType) {
  const isQuote = requestType === 'quote';
  trackLead({
    leadType: isQuote ? 'quote_request' : 'contact_form',
    method: 'Website form',
    adsLabel: isQuote
      ? process.env.REACT_APP_GOOGLE_ADS_QUOTE_LABEL
      : process.env.REACT_APP_GOOGLE_ADS_CONTACT_LABEL,
  });
}

export function sendWebVital(metric) {
  if (!metric?.name) return;
  const rawValue = metric.name === 'CLS' ? metric.value * 1000 : metric.value;
  sendEvent('web_vital', {
    event_category: 'Web Vitals',
    event_label: metric.id,
    value: Math.round(rawValue),
    metric_delta: metric.delta,
    non_interaction: true,
  });
}
